import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { OfferOption } from 'ng2-app/models/dto/offer-option/offer-option.model';
import { Store } from '@ngxs/store';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PatchOfferOptionStatus } from 'ng2-app/store/offers/offer-options.actions';
import { map, take } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OfferOptionSnackBarType } from 'ng2-app/offer-option/offer-option-snack-bar-data/offer-option-snack-bar-data.interface';
import { OfferOptionSnackBarDataComponent } from 'ng2-app/offer-option/offer-option-snack-bar-data/offer-option-snack-bar-data.component';
import { DealState } from 'ng2-app/store/deal/deal/deal.state';
import { SaveDealStatus } from 'ng2-app/store/deal/deal/deal.actions';
import { DealUtility } from 'ng2-app/util/deal/deal.utility';

@Component({
  selector: 'app-offer-option-publish-dialog',
  templateUrl: './offer-option-publish-dialog.component.html',
  styleUrls: ['./offer-option-publish-dialog.component.scss'],
  standalone: false
})
export class OfferOptionPublishDialogComponent implements OnInit {
  @Input() option: OfferOption;
  @Input() optionType: string;

  @Output() offerOptionPublished = new EventEmitter<object>();

  optionParentLenderName: string;
  optionParentName: string;
  optionParentType: string;
  optionLoanType: string;
  optionLoanCategoryType: string;
  termDurationFormatted: string;
  paymentFrequencyShort: string;
  payback: string | number | null;
  costOfCapital: number | null;

  ///////////
  // PERIODIC RATE
  ///////////
  periodicRate: string = '';
  periodicLabel: string = '';

  constructor(
    private store: Store,
    @Inject(MAT_DIALOG_DATA) public data,
    private dialogRef: MatDialogRef<OfferOptionPublishDialogComponent>,
    private titlecasePipe: TitleCasePipe,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.optionLoanCategoryType =
      this.data?.option?.deal?.loan_product?.category?.type || '';
    this.optionLoanType = this.getOptionLoanType();

    if (this.data?.optionType === 'approval') {
      this.optionParentLenderName =
        this.data?.option?.approval?.['loanProductLender'] || '';
      this.optionParentName = this.data?.option?.approval?.['name'] || '';
      this.optionParentType = this.data?.option?.approval?.['stability'] || '';
    }

    switch (this.data?.option?.termSet?.termType) {
      case 'months':
        this.termDurationFormatted = 'Month';
        break;
      case 'weeks':
        this.termDurationFormatted = 'Week';
        break;
      case 'days':
        this.termDurationFormatted = 'Day';
        break;
      default:
        this.termDurationFormatted = '';
        break;
    }

    if (this.termDurationFormatted && this.data?.option?.termSet?.term > 1) {
      this.termDurationFormatted += 's';
    }

    const frequencyMap = {
      daily: 'day',
      semiweekly: 'sm.',
      weekly: 'wk.',
      biweekly: 'bw.',
      semimonthly: 'sm.',
      monthly: 'mo.',
      bimonthly: 'bm.',
    };
    this.paymentFrequencyShort =
      frequencyMap[this.data?.option?.termSet?.paymentFrequency] || '';

    this.payback = this.getPaybackAmount();
    this.costOfCapital = this.calculateCostOfCapital(
      this.data?.option?.termSet?.amount || this.data?.option?.termSet?.initialDrawAmount,
      this.payback
    );

    if (['loc', 'term'].includes(this.optionLoanCategoryType)) {
      this.periodicLabel =
        this.titlecasePipe.transform(
          this.data?.option?.termSet?.paymentFrequency
        ) + ' rate';
      this.periodicRate =
        parseFloat(this.data?.option?.termSet?.periodicRate ?? 0).toFixed(2) +
        '%';
    }
  }


  closeModal() {
    this.dialogRef.close();
  }

  publishOption() {
    const dealId = this.data.option.dealId;
    const deal$ = this.store.select(DealState.deals).pipe(map((deals) => deals.find((deal) => deal.id === dealId)));
    const offerOption = this.data.option;
    const optionType = this.data.optionType;
    this.store
      .dispatch(
        new PatchOfferOptionStatus(
          offerOption.dealId,
          offerOption.id,
          'optionToBorrower',
          true
        )
      )
      .pipe(take(1))
      .subscribe(() => {
        this.openOfferSnackBar(
          offerOption,
          optionType,
          true,
          OfferOptionSnackBarType.PUBLISH
        );

        deal$.pipe(take(1))
          .subscribe((deal) => {
            if (deal?.stage === 'underwriting' && deal?.status === 'offerReceived') {
              this.store.dispatch(new SaveDealStatus(deal.id, 'underwriting', 'offerToBorrower', '', [], []))
                .subscribe();
            }
          });
      });
    this.offerOptionPublished.emit();
    this.closeModal();
  }

  openOfferSnackBar(
    offerOption: OfferOption,
    optionType: string,
    isSuccess: boolean,
    type: OfferOptionSnackBarType
  ) {
    this.snackBar.openFromComponent(OfferOptionSnackBarDataComponent, {
      data: {
        offerOption,
        optionType,
        isSuccess: isSuccess,
        type,
      },
      duration: 3000,
    });
  }

  private getPaybackAmount() {
    if (this.optionLoanCategoryType === 'loc') {
      return null;
    }

    const term = this.data?.option?.termSet ?? {};
    let amount = term.initialDrawAmount || term.amount;
    if (!amount) {
      return null;
    }
    amount = parseFloat(amount);
    if (!amount || !Number.isFinite(amount)) {
      return null;
    }

    let payback = term.payback;
    if (payback) {
      payback = parseFloat(payback);
    }

    if (payback && Number.isFinite(payback) && payback >= amount) {
      return payback;
    }

    if (term.apr) {
      return null;
    }

    const factorRate = Number(term.factorRate);
    // calculate payback only for valid factor rate
    if (factorRate < 1 && factorRate >= 2) {
      return null;
    }
    return amount * factorRate;
  }

  private calculateCostOfCapital(optionAmount, paybackAmount) {
    if (!optionAmount || !paybackAmount) {
      return null;
    }
    const amount = parseFloat(optionAmount);
    const payback = parseFloat(paybackAmount);
    if (
      !Number.isFinite(amount) ||
      !Number.isFinite(payback) ||
      payback < amount
    ) {
      return null;
    }

    return payback - amount;
  }

  private getOptionLoanType() {
    const typeMap = {
      ach: 'Advance',
      loc: 'Line of credit',
      term: 'Term loan',
    };
    return DealUtility.getOverriddenLoanType(this.data?.option?.deal) ??
      typeMap[this.optionLoanCategoryType] ??
      this.data?.option?.deal?.loan_product?.category?.shortName ??
      '';
  }
}
